import React, { useEffect, useState, useMemo } from "react";
import { Box, Typography, Tabs, Tab, CircularProgress, Button } from "@mui/material";
import { getDashboardCharts, getMasterData } from "../../services/apis";
import { useApplicationContext } from "../../context/applicationContext";
import { LineChart, PieChart } from "@mui/x-charts";
import moment from "moment";
import Grid from "@mui/material/Grid2";
import AppsIcon from "@mui/icons-material/Apps";
import EmailIcon from "@mui/icons-material/Email";
import AnnouncementIcon from "@mui/icons-material/Announcement";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import { useTheme } from "@emotion/react";
import { stringToColor } from "../../utils";

const dashbordStyles = {
	flexGrow: 1,
	p: 3,
	m: 3,
	ml: 0,
	mr: 0,
	backgroundColor: "#fff",
	overflow: "auto",
	borderRadius: 2,
};

const headingStyles = { fontSize: "18px", fontWeight: "bold" };

const stackStrategy = {
	// stack: "total",
	area: false,
	stackOffset: "none",
};

const customize = {
	height: 400,
	legend: { hidden: true },
	margin: { top: 5 },
};

const trendTabs = [
	{
		label: "All",
		value: "all",
	},
	{
		label: "Today",
		value: "today",
	},
	{
		label: "This week",
		value: "week",
	},
	{
		label: "This month",
		value: "month",
	},
	{
		label: "This year",
		value: "year",
	},
];

const iconStyles = {
	backgroundColor: "#FFFFFF",
	borderRadius: 50,
	color: "#0996d7",
	display: "flex",
	alignItems: "center",
	padding: 1,
	marginRight: 1,
};

const statsTextStyles = { fontWeight: "bold", fontSize: "1rem", textTransform: "capitalize" };

const iconsMap = {
	total_apps: (
		<Box sx={iconStyles}>
			<AppsIcon />
		</Box>
	),
	total_users: (
		<Box sx={iconStyles}>
			<PeopleAltIcon />
		</Box>
	),
	total_feedbacks: (
		<Box sx={iconStyles}>
			<EmailIcon />
		</Box>
	),
	pending_requests: (
		<Box sx={iconStyles}>
			<AnnouncementIcon />
		</Box>
	),
};

const Dashboard = () => {
	const [masterData, setMasterData] = useState(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [appTrendData, setAppTrendData] = useState(null);
	const [appUsageData, setAppUsageData] = useState(null);
	const [selectedTrendTab, setSelectedTrendTab] = useState(0);
	const [trendLoading, setTrendLoading] = useState(false);

	const theme = useTheme();
	const { setAppList } = useApplicationContext();

	const valueFormatter = item => `${item.value}%`;

	const handleSelectTrend = async (event, value) => {
		setTrendLoading(true);
		console.log("Selected trend:", trendTabs[value].value);
		setSelectedTrendTab(value);
		setTrendLoading(false);
	};

	const generateAppTrendData = data => {
		const appTrend = data.app_trend;
		const apps = data.app_names;

		const filledData = appTrend.map(entry => {
			const newEntry = { date: entry.date || "N/A" };
			apps.forEach(app => {
				newEntry[app] = isNaN(entry[app]) ? 0 : entry[app];
			});
			return newEntry;
		});

		const series = apps.map((app, idx) => ({
			dataKey: app,
			label: app,
			color: stringToColor(app),
			showMark: false,
			...stackStrategy,
		}));

		setAppTrendData({ dataset: filledData, series });
	};

	const handleGetMasterData = async () => {
		setLoading(true);
		try {
			const response = await getMasterData();
			setAppUsageData(response.data.app_usage);
			setAppList(response.data.app_list);
			setMasterData(response.data);
		} catch (error) {
			setError(error);
		}
		setLoading(false);
	};

	const handleGetDashboardCharts = async () => {
		setTrendLoading(true);
		try {
			const response = await getDashboardCharts({
				period: trendTabs[selectedTrendTab].value,
			});
			generateAppTrendData(response.data);
		} catch (error) {
			setError(error);
		}
		setTrendLoading(false);
	};

	useEffect(() => {
		handleGetMasterData();
	}, []);

	useEffect(() => {
		handleGetDashboardCharts();
	}, [selectedTrendTab]);

	const memoizedData = useMemo(() => {
		return appTrendData?.dataset.map(item => ({
			...item,
			date: moment(item.date).valueOf(),
		}));
	}, [appTrendData?.dataset]);

	const memoizedSeries = useMemo(() => appTrendData?.series, [appTrendData?.series]);

	return (
		<Box>
			<Box component="main" sx={{ ...dashbordStyles, mt: 8, mb: 0 }}>
				{loading ? (
					<Box display="flex" justifyContent="center" alignItems="center" height={440}>
						<CircularProgress />
					</Box>
				) : (
					masterData &&
					masterData.stats && (
						<Box>
							{Object.entries(masterData.stats).map(([key, value]) => {
								return (
									<Button
										key={key}
										component="label"
										role={undefined}
										variant="contained"
										tabIndex={-1}
										startIcon={iconsMap[key]}
										sx={{
											padding: "15px 20px",
											marginRight: 3,
											borderRadius: 2,
											"& .MuiSvgIcon-root": {
												fontSize: "2.2rem !important",
											},
										}}>
										<Box
											display={"flex"}
											justifyContent={"center"}
											flexDirection={"column"}>
											<Typography variant="h6" sx={statsTextStyles}>
												{key.replace(/_/g, " ")}
											</Typography>
											<Typography
												variant="h6"
												sx={{ ...statsTextStyles, fontSize: "1.2rem" }}>
												{value}
											</Typography>
										</Box>
									</Button>
								);
							})}
						</Box>
					)
				)}
			</Box>
			<Box component="main">
				<Grid container xs={12} spacing={2} justifyContent={"space-between"}>
					<Grid item="true" xs={9} sx={{ ...dashbordStyles, width: "68%" }}>
						<Box
							display={"flex"}
							flexDirection={"row"}
							alignItems={"center"}
							justifyContent={"space-between"}>
							<Typography sx={headingStyles}>App trend</Typography>
							<Tabs value={selectedTrendTab} onChange={handleSelectTrend}>
								{trendTabs.map(tab => (
									<Tab
										key={tab.value}
										id={tab.value}
										label={tab.label}
										disabled={trendLoading}
									/>
								))}
							</Tabs>
						</Box>
						{trendLoading ? (
							<Box
								display="flex"
								justifyContent="center"
								alignItems="center"
								height={440}>
								<CircularProgress />
							</Box>
						) : (
							<Box sx={{ width: "100%" }}>
								{appTrendData && appTrendData.dataset.length > 0 && (
									<LineChart
										loading={trendLoading}
										xAxis={[
											{
												dataKey: "date",
												scaleType: "time",
												valueFormatter: v => moment(v).format("YYYY-MM-DD"),
											},
										]}
										series={memoizedSeries}
										dataset={memoizedData}
										{...customize}
									/>
								)}
							</Box>
						)}
					</Grid>

					<Grid
						item="true"
						xs={3}
						sx={{
							...dashbordStyles,
						}}>
						<Typography sx={headingStyles}>App usage</Typography>
						{loading ? (
							<Box
								display="flex"
								justifyContent="center"
								alignItems="center"
								height={440}>
								<CircularProgress />
							</Box>
						) : (
							<Box
								sx={{
									justifyContent: "center",
									alignItems: "center",
									width: "100%",
								}}>
								{appUsageData && appUsageData.length > 0 && (
									<PieChart
										loading={loading}
										height={300}
										series={[
											{
												data: appUsageData.map(item => ({
													...item,
													color: stringToColor(item.label),
												})),
												innerRadius: 50,
												arcLabel: null,
												arcLabelMinAngle: 20,
												valueFormatter,
											},
										]}
										skipAnimation={false}
										slotProps={{
											legend: { hidden: true },
										}}
										margin={{ left: 50 }}
									/>
								)}
							</Box>
						)}
					</Grid>
				</Grid>
			</Box>
		</Box>
	);
};

export default Dashboard;
